import React, { useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { messages } from '../../messages';
import { CUSTOMER_ACCESS, type CustomerAccess } from '../../types';
import { OpenIcon, RestrictedIcon } from './icons';

const classNamePrefix = 'customer-access-dropdown';

type Props = {
	initialValue?: CustomerAccess;
	onCustomerAccessChange: (customerAccess: CustomerAccess) => void;
};

type OptionType = {
	label: string;
	value: CustomerAccess;
	icon: ReactNode;
	description: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderOption = (option: OptionType, { context }: any) => {
	if (context === 'menu') {
		return (
			<OptionWrapper data-testid={`${classNamePrefix}__value__${option.value}`}>
				<IconWrapper>{option.icon}</IconWrapper>
				<Box xcss={contentWrapperStyles}>
					<TitleWrapper>{option.label}</TitleWrapper>
					<DescriptionWrapper>{option.description}</DescriptionWrapper>
				</Box>
			</OptionWrapper>
		);
	}
	return (
		<IconControlWrapper>
			<IconWrapper>{option.icon}</IconWrapper>
			{option.label}
		</IconControlWrapper>
	);
};

export const CustomerAccessDropdown = ({ onCustomerAccessChange, initialValue }: Props) => {
	const { formatMessage } = useIntl();

	const openOption: OptionType = {
		label: formatMessage(messages.openLabel),
		value: CUSTOMER_ACCESS.PUBLIC,
		icon: <OpenIcon />,
		description: formatMessage(messages.openDescription),
	};

	const restrictedOption: OptionType = {
		label: formatMessage(messages.restrictedLabel),
		value: CUSTOMER_ACCESS.RESTRICTED,
		icon: <RestrictedIcon />,
		description: formatMessage(messages.restrictedDescription),
	};

	const options = [restrictedOption, openOption];
	const [selected, setSelected] = useState<OptionType | undefined>(() => {
		if (initialValue) {
			return initialValue === CUSTOMER_ACCESS.RESTRICTED ? restrictedOption : openOption;
		}
		return undefined;
	});

	const onChange = (option: OptionType | null) => {
		if (option && option.value !== selected?.value) {
			setSelected(option);
			onCustomerAccessChange(option.value);
		}
	};

	return (
		<Select
			name="servicedesk-customer-permissions.common.ui.customer-access-dropdown"
			data-testId="servicedesk-customer-permissions.common.ui.customer-access-dropdown"
			aria-label={formatMessage(messages.placeholder)}
			inputId="CustomerAccessDropdownType"
			classNamePrefix={classNamePrefix}
			options={options}
			value={selected}
			onChange={onChange}
			placeholder={formatMessage(messages.placeholder)}
			formatOptionLabel={renderOption}
		/>
	);
};

const contentWrapperStyles = xcss({
	marginRight: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionWrapper = styled.p({
	font: token('font.body.small'),
	lineHeight: '16px',
	marginTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconControlWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: '24px',
	height: '24px',
	marginLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: '24px',
	height: '24px',
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionWrapper = styled.div({
	display: 'flex',
	padding: token('space.100', '8px'),
	paddingBottom: token('space.200', '16px'),
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleWrapper = styled.div({
	font: token('font.body'),
	marginBottom: token('space.100', '8px'),
});
